import { NgIf, NgFor } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NbDialogRef, NbDialogService, NbToastrService, NbCardModule, NbIconModule, NbButtonModule, NbCheckboxModule, NbTooltipModule, NbInputModule } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ViewContentComponent } from 'src/app/features/content/content-table/view-content/view-content.component';
import { ContentState } from 'src/app/features/content/redux/content.state';
import { ViewInsightComponent } from 'src/app/features/insights/insights-table/view-insight/view-insight.component';
import { InsightsState } from 'src/app/features/insights/redux';
import { SurveysState } from 'src/app/features/surveys/redux';
import { ViewSurveyComponent } from 'src/app/features/surveys/surveys-table/view-survey/view-survey.component';
import { Content } from 'src/app/models/content';
import { Insight } from 'src/app/models/insight';
import { Survey } from 'src/app/models/survey';
import { RouterState } from '../../../auth/redux';
import { CampaignState, DeepDeleteCampaign, LoadAffectedCampaigns } from '../../redux';

@Component({
    selector: 'app-delete-campaign',
    templateUrl: './delete-campaign.component.html',
    styleUrls: ['./delete-campaign.component.css'],
    imports: [NbCardModule, NbIconModule, NbButtonModule, NgIf, FormsModule, NbCheckboxModule, NbTooltipModule, NgFor, NbInputModule]
})
export class DeleteCampaignComponent implements OnInit, OnDestroy {
  public campaign$: Observable<any[]> = inject(Store).select(CampaignState.campaign);
  public insight$: Observable<any[]> = inject(Store).select(InsightsState.allInsights);
  public content$: Observable<any[]> = inject(Store).select(ContentState.content);
  public survey$: Observable<any[]> = inject(Store).select(SurveysState.surveys);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  public affectedCampaign$: Observable<any[]> = inject(Store).select(CampaignState.affectedCampaigns);
  public spin$: Observable<boolean> = inject(Store).select(CampaignState.spin);
  private unsubscribe = new Subject<void>();

  public campaigns = [];
  public insights = [];
  public contents = [];
  public surveys = [];

  public affectedCampaigns;
  public campaign;

  public spin;
  public username = '';
  public delete = {
    insight: false,
    content: false,
    surveys: {}
  };
  public confirmation = '';

  constructor(
    protected ref: NbDialogRef<DeleteCampaignComponent>,
    private store: Store,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService
  ) {}

  public ngOnInit() {
    this.campaign$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.campaigns = data));
    this.insight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.insights = data));
    this.content$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.contents = data));
    this.survey$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.surveys = data));
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.affectedCampaign$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.affectedCampaigns = data));
    this.spin$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.spin = data));

    this.store.dispatch(new LoadAffectedCampaigns(this.campaign));

    this.campaign.responses.forEach((response) => {
      this.delete.surveys[response.id] = false;
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public deepDelete() {
    const payload = {
      campaign_id: this.campaign.id,
      deleted_by: this.username,
      delete_insight: this.delete.insight,
      delete_content: this.delete.content,
      delete_surveys: Object.keys(this.delete.surveys).filter((survey_id) => this.delete.surveys[survey_id])
    };
    this.store.dispatch(new DeepDeleteCampaign(payload));
    this.ref.close();
  }

  public close() {
    this.ref.close();
  }

  public viewContent(event) {
    const found = this.contents.find((element) => element.content_id === event);
    if (found) {
      this.dialogService.open(ViewContentComponent, {
        context: {
          content: Content.copy(found) as Content
        }
      });
    } else {
      this.toastrService.show(status, $localize`Content with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public viewInsight(event) {
    const found = this.insights.find((element) => element.id === event);
    if (found) {
      this.dialogService.open(ViewInsightComponent, {
        context: {
          insight: Insight.copy(found) as Insight
        }
      });
    } else {
      this.toastrService.show(status, $localize`Insight with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public viewSurvey(event) {
    const found = this.surveys.find((element) => element.survey_id === event);
    if (found) {
      this.dialogService.open(ViewSurveyComponent, {
        context: {
          survey: Survey.copy(found) as Survey
        }
      });
    } else {
      this.toastrService.show(status, $localize`Survey with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public surveysIncludes(responseId) {
    return this.surveys.find((survey) => survey.survey_id === responseId) !== undefined;
  }

  public contentsIncludes(contentId) {
    return this.contents.find((content) => content.content_id === contentId) !== undefined;
  }

  public insightsIncludes(insightId) {
    return this.insights.find((insight) => insight.id === insightId) !== undefined;
  }

  public anyCampaignsActive(insightId?, contentId?, surveyId?) {
    let active = 0;

    if (insightId) {
      this.affectedCampaigns.insight.forEach((campaign) => {
        if (campaign.enabled) {
          active += 1;
        }
      });
    }
    if (contentId) {
      this.affectedCampaigns.content.forEach((campaign) => {
        if (campaign.enabled) {
          active += 1;
        }
      });
    }
    if (surveyId) {
      this.affectedCampaigns.surveys[surveyId].forEach((campaign) => {
        if (campaign.enabled) {
          active += 1;
        }
      });
    }

    return active;
  }
}
